import React, { useState, useEffect } from 'react';
import Modal from '../shared/Modal';

interface AddDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  organization?: any; // For editing existing organization
}

interface FormData {
  name: string;
  subdomain: string;
  logo: string;
  active: boolean;
  allowed_domains: string[];
  allowed_ips: string[];
  require_api_whitelist: boolean;
}

interface FormErrors {
  name?: string;
  subdomain?: string;
  allowed_domains?: string;
  allowed_ips?: string;
}

const AddDataModal: React.FC<AddDataModalProps> = ({ isOpen, onClose, onSave, organization }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    subdomain: '',
    logo: '',
    active: true,
    allowed_domains: [],
    allowed_ips: [],
    require_api_whitelist: false
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [allowedDomainsText, setAllowedDomainsText] = useState('');
  const [allowedIpsText, setAllowedIpsText] = useState('');

  // Populate form when editing an organization
  useEffect(() => {
    if (organization && isOpen) {
      setFormData({
        name: organization.name || '',
        subdomain: organization.subdomain || '',
        logo: organization.logo || '',
        active: organization.active ?? true,
        allowed_domains: organization.allowed_domains || [],
        allowed_ips: organization.allowed_ips || [],
        require_api_whitelist: organization.require_api_whitelist ?? false,
      });
      // Convert arrays to JSON string format for display
      setAllowedDomainsText(organization.allowed_domains ? JSON.stringify(organization.allowed_domains) : '');
      setAllowedIpsText(organization.allowed_ips ? JSON.stringify(organization.allowed_ips) : '');
    } else if (!isOpen) {
      // Reset form when modal closes
      setFormData({
        name: '',
        subdomain: '',
        logo: '',
        active: true,
        allowed_domains: [],
        allowed_ips: [],
        require_api_whitelist: false,
      });
      setAllowedDomainsText('');
      setAllowedIpsText('');
      setErrors({});
      setIsLoading(false);
    }
  }, [organization, isOpen]);

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleDomainsChange = (value: string) => {
    setAllowedDomainsText(value);
    // Clear error when user starts typing
    if (errors.allowed_domains) {
      setErrors(prev => ({ ...prev, allowed_domains: undefined }));
    }
  };

  const handleIpsChange = (value: string) => {
    setAllowedIpsText(value);
    // Clear error when user starts typing
    if (errors.allowed_ips) {
      setErrors(prev => ({ ...prev, allowed_ips: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Organization name is required';
    }

    // Validate JSON for allowed domains
    try {
      if (allowedDomainsText.trim()) {
        const parsed = JSON.parse(allowedDomainsText.trim());
        if (!Array.isArray(parsed)) {
          newErrors.allowed_domains = 'Must be a valid JSON array (e.g., ["example.com", "subdomain.example.com"])';
        }
      }
    } catch {
      newErrors.allowed_domains = 'Must be a valid JSON array (e.g., ["example.com", "subdomain.example.com"])';
    }

    // Validate JSON for allowed IPs
    try {
      if (allowedIpsText.trim()) {
        const parsed = JSON.parse(allowedIpsText.trim());
        if (!Array.isArray(parsed)) {
          newErrors.allowed_ips = 'Must be a valid JSON array (e.g., ["192.168.1.1", "10.0.0.1"])';
        }
      }
    } catch {
      newErrors.allowed_ips = 'Must be a valid JSON array (e.g., ["192.168.1.1", "10.0.0.1"])';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      // Parse the JSON inputs
      const allowedDomains = allowedDomainsText.trim() 
        ? JSON.parse(allowedDomainsText.trim())
        : [];
      
      const allowedIps = allowedIpsText.trim()
        ? JSON.parse(allowedIpsText.trim())
        : [];

      const dataToSave = {
        ...formData,
        allowed_domains: allowedDomains,
        allowed_ips: allowedIps,
        ...(organization && { id: organization.id }) // Include ID for updates
      };

      await onSave(dataToSave);
      
      // Reset form
      setFormData({
        name: '',
        subdomain: '',
        logo: '',
        active: true,
        allowed_domains: [],
        allowed_ips: [],
        require_api_whitelist: false
      });
      setAllowedDomainsText('');
      setAllowedIpsText('');
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Error creating organization:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Failed to create organization: ${errorMessage}`);
      throw error; // Re-throw to prevent modal from closing
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={organization ? "Edit Organization" : "Add New Organization"}>
      <div className="space-y-6">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Organization Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.name ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Enter organization name"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        {/* Subdomain */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Subdomain
          </label>
          <input
            type="text"
            value={formData.subdomain}
            onChange={(e) => handleInputChange('subdomain', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.subdomain ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Enter subdomain (optional)"
          />
          {errors.subdomain && (
            <p className="mt-1 text-sm text-red-600">{errors.subdomain}</p>
          )}
        </div>

        {/* Logo */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Logo URL
          </label>
          <input
            type="url"
            value={formData.logo}
            onChange={(e) => handleInputChange('logo', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="https://example.com/logo.png"
          />
        </div>

        {/* Active Toggle */}
        <div className="flex items-center space-x-3">
          <label className="block text-sm font-medium text-gray-700">
            Active
          </label>
          <button
            type="button"
            onClick={() => handleInputChange('active', !formData.active)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              formData.active ? 'bg-blue-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.active ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <span className="text-sm text-gray-500">
            {formData.active ? 'Enabled' : 'Disabled'}
          </span>
        </div>

        {/* Allowed Domains */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Allowed Domains
          </label>
          <textarea
            value={allowedDomainsText}
            onChange={(e) => handleDomainsChange(e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.allowed_domains ? 'border-red-300' : 'border-gray-300'
            }`}
            rows={3}
            placeholder='["example.com", "subdomain.example.com"]'
          />
          <p className="mt-1 text-xs text-gray-500">
            Enter as JSON array format. Example: ["example.com", "subdomain.example.com"]
          </p>
          {errors.allowed_domains && (
            <p className="mt-1 text-sm text-red-600">{errors.allowed_domains}</p>
          )}
        </div>

        {/* Allowed IPs */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Allowed IPs
          </label>
          <textarea
            value={allowedIpsText}
            onChange={(e) => handleIpsChange(e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.allowed_ips ? 'border-red-300' : 'border-gray-300'
            }`}
            rows={3}
            placeholder='["192.168.1.1", "10.0.0.1"]'
          />
          <p className="mt-1 text-xs text-gray-500">
            Enter as JSON array format. Example: ["192.168.1.1", "10.0.0.1"]
          </p>
          {errors.allowed_ips && (
            <p className="mt-1 text-sm text-red-600">{errors.allowed_ips}</p>
          )}
        </div>

        {/* API Whitelist Toggle */}
        <div className="flex items-center space-x-3">
          <label className="block text-sm font-medium text-gray-700">
            Require API Whitelist
          </label>
          <button
            type="button"
            onClick={() => handleInputChange('require_api_whitelist', !formData.require_api_whitelist)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              formData.require_api_whitelist ? 'bg-blue-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.require_api_whitelist ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <span className="text-sm text-gray-500">
            {formData.require_api_whitelist ? 'Enabled' : 'Disabled'}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (organization ? 'Updating...' : 'Adding...') : (organization ? 'Update Data' : 'Add Data')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddDataModal;